import {
  fetchWithTimeout,
  parseErrorResponse,
  REGISTER_TIMEOUT,
  BULK_FETCH_TIMEOUT,
} from './base.ts';

/** #1777 — screenshot als base64 (data-URL mag ook); de server keurt het type op de inhoud. */
export interface SupportBijlageUpload {
  name: string;
  data: string;
}

/** #1777 — wat de app automatisch meestuurt (geen project). */
export interface SupportClientContext {
  pagina?: string;
  appVersie?: string;
  browser?: string;
  scherm?: string;
}

/** Met screenshots kan het versturen op een trage lijn langer duren dan 15 s. */
const timeoutVoor = (bijlagen?: SupportBijlageUpload[]) =>
  bijlagen?.length ? BULK_FETCH_TIMEOUT : REGISTER_TIMEOUT;

/** URL van een screenshot bij een ticket (cookie-auth, alleen de eigenaar). */
export const supportBijlageUrl = (ticketId: string, bijlageId: string) =>
  `/api/support/tickets/${ticketId}/attachments/${bijlageId}`;

export const supportApi = {
  /**
   * Get all support tickets for the authenticated user.
   */
  getSupportTickets: async (): Promise<any> => {
    const res = await fetchWithTimeout('/api/support/tickets');
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Get a single support ticket with its messages.
   */
  getSupportTicket: async (ticketId: string): Promise<any> => {
    const res = await fetchWithTimeout(`/api/support/tickets/${ticketId}`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Create a new support ticket with an initial message.
   */
  createSupportTicket: async (data: {
    subject: string;
    category: string;
    message: string;
    /** #1764 — vast per formulier; de server maakt bij hetzelfde kenmerk geen tweede ticket. */
    client_kenmerk?: string;
    bijlagen?: SupportBijlageUpload[];
    client_context?: SupportClientContext;
  }): Promise<any> => {
    // #1756 — 15 s i.p.v. 5: bij een trage database gold het ticket als mislukt
    // terwijl het al bestond, en dan maakt de gebruiker het opnieuw aan.
    const res = await fetchWithTimeout(
      '/api/support/tickets',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
      timeoutVoor(data.bijlagen)
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Add a reply message to an existing ticket.
   */
  addTicketMessage: async (
    ticketId: string,
    message: string,
    bijlagen?: SupportBijlageUpload[]
  ): Promise<any> => {
    const res = await fetchWithTimeout(
      `/api/support/tickets/${ticketId}/messages`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, ...(bijlagen?.length ? { bijlagen } : {}) }),
      },
      timeoutVoor(bijlagen)
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /**
   * Rate a resolved/closed ticket (satisfaction).
   */
  rateTicket: async (ticketId: string, rating: 'positive' | 'negative'): Promise<any> => {
    const res = await fetchWithTimeout(`/api/support/tickets/${ticketId}/satisfaction`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ rating }),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },
};
